import { useState, useCallback, useRef, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';

export type GalleryPermissionState =
  | 'idle'
  | 'granted'
  | 'denied'               // denied once – can ask again
  | 'permanently-denied';  // "Don't ask again" – must open Settings

interface UseGalleryPermissionReturn {
  permissionState: GalleryPermissionState;
  /** Request the permission; if granted, execute `onGranted` immediately. */
  requestAndProceed: (onGranted: () => void) => Promise<void>;
  /** Re-run the permission request with the previously stored callback. */
  retry: () => void;
  /** Reset the state back to idle (e.g. on dialog dismiss). */
  resetPermission: () => void;
}

/**
 * Handles Android runtime permission for gallery/photo access before
 * triggering a file-input click.
 *
 *  - Android 13+ → requests READ_MEDIA_IMAGES (via @capacitor/camera "photos")
 *  - Android <13  → requests READ_EXTERNAL_STORAGE
 *  - Web / iOS    → skips permission logic, calls onGranted directly
 */
export function useGalleryPermission(): UseGalleryPermissionReturn {
  const [permissionState, setPermissionState] = useState<GalleryPermissionState>('idle');
  const pendingCallback = useRef<(() => void) | null>(null);

  // ── Proactive request on mount ───────────────────────────────────────────
  // Show the system permission dialog as soon as the page that uses this hook
  // mounts — so the user is not surprised when they tap the camera button.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      setPermissionState('granted');
      return;
    }

    Camera.checkPermissions()
      .then((current) => {
        if (current.photos === 'granted') {
          setPermissionState('granted');
          return null;
        }
        // Show the system dialog immediately on page load
        return Camera.requestPermissions({ permissions: ['photos'] });
      })
      .then((result) => {
        if (!result) return; // already granted branch
        if (result.photos === 'granted') {
          setPermissionState('granted');
        } else if (result.photos === 'denied') {
          // OS returned denied without dialog → permanently denied
          setPermissionState('permanently-denied');
        } else {
          // 'prompt-with-rationale' → denied once, can ask again on button tap
          setPermissionState('denied');
        }
      })
      .catch(() => {
        // Ignore — the button-tap flow will handle errors
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestAndProceed = useCallback(async (onGranted: () => void) => {
    // Persist callback so the user can retry without passing it again
    pendingCallback.current = onGranted;

    // On web or iOS the browser/system handles permissions natively
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      setPermissionState('granted');
      onGranted();
      return;
    }

    try {
      const current = await Camera.checkPermissions();
      const photoStatus = current.photos;

      if (photoStatus === 'granted') {
        setPermissionState('granted');
        onGranted();
        return;
      }

      const result = await Camera.requestPermissions({ permissions: ['photos'] });
      const granted = result.photos;

      if (granted === 'granted') {
        setPermissionState('granted');
        onGranted();
      } else if (granted === 'denied') {
        // OS returned 'denied' without showing a dialog → permanently denied
        setPermissionState('permanently-denied');
      } else {
        // 'prompt-with-rationale' → denied once, can still ask again
        setPermissionState('denied');
      }
    } catch (err) {
      // Fail open so the user can still attempt to pick a file
      console.error('[useGalleryPermission] Permission error:', err);
      setPermissionState('granted');
      onGranted();
    }
  }, []);

  const retry = useCallback(() => {
    if (pendingCallback.current) {
      requestAndProceed(pendingCallback.current);
    }
  }, [requestAndProceed]);

  const resetPermission = useCallback(() => {
    setPermissionState('idle');
  }, []);

  return { permissionState, requestAndProceed, retry, resetPermission };
}
